import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { datadogRum } from '@datadog/browser-rum';
import { useDecision } from '@optimizely/react-sdk';
import { PROGRAM_RECS } from '@src/constants/featureFlags';

export enum ProductUsableComponents {
	QUIZ_SHELF = 'quiz shelf',
	PERSONALIZED_SHELF = 'personalized shelf',
	RESULTS_LIST = 'results list',
}

export interface ProductUsableContextInterface {
	componentLoaded: (component: ProductUsableComponents) => void;
}

export const ProductUsableContext = createContext<ProductUsableContextInterface | undefined>(
	undefined
);

export const useProductUsable = () => {
	const context = useContext(ProductUsableContext);

	if (context === undefined) {
		throw new Error('useProductUsable must be used within ProductUsableProvider.');
	}

	return context;
};

export const ProductUsableProvider: React.FC = ({ children }) => {
	const [programRecsDecision] = useDecision(PROGRAM_RECS);
	const [loadedComponents, setLoadedComponents] = useState([]);

	const programRecsLayout = programRecsDecision.variables?.layout;

	const componentsToLoad = useMemo(
		() =>
			[
				ProductUsableComponents.QUIZ_SHELF,
				ProductUsableComponents.RESULTS_LIST,
				programRecsLayout === 'one_tab' ? ProductUsableComponents.PERSONALIZED_SHELF : null,
			].filter(Boolean),
		[programRecsLayout]
	);

	useEffect(() => {
		if (componentsToLoad.every(component => loadedComponents.includes(component))) {
			datadogRum.addTiming('product_usable');
		}
	}, [componentsToLoad, loadedComponents]);

	const componentLoaded = (component: ProductUsableComponents) => {
		if (!loadedComponents.includes(component)) {
			return setLoadedComponents(prev => [...prev, component]);
		}
	};

	return (
		<ProductUsableContext.Provider value={{ componentLoaded }}>
			{children}
		</ProductUsableContext.Provider>
	);
};
